<template>
    <div class="compare-page-container" :class="hasItems ? 'lg:tw-container lg:tw-px-6 tw-mx-auto' : null">
        <div
            class="compare-page"
            :class="hasItems ? 'lg:tw-border-l lg:tw-border-r tw-border-solid tw-border-border-color' : null"
        >
            <loading-wrapper :loading="isLoading">
                <template v-if="!hasItems">
                    <slot v-bind="{ onCompareHeroButtonClick, items }" name="hero"></slot>
                </template>

                <template v-if="hasItems && !alreadyInteracted">
                    <slot v-bind="{ openCompareModal, items, onMenuClick, openLeadModal, pricingsPollingUrl }"></slot>
                </template>

                <template>
                    <ds-polling-wrapper
                        ref="pollingWrapper"
                        :url="pricingsPollingUrl"
                        :start-on-mount="true"
                        @final-data-loaded="onFinalDataLoaded"
                    >
                        <template v-if="hasItems && alreadyInteracted" #default="{ isPending }">
                            <compare-table
                                :items="items"
                                :sections="sections"
                                :busy="isLoading"
                                :pricing-list="pricingList"
                                :is-polling-pending="isPending"
                                @add-vehicle-click="openCompareModal(null, items[items.length - 1])"
                                @menu-click="onMenuClick"
                                @compare-quotes-click="openLeadModal"
                            />
                        </template>
                    </ds-polling-wrapper>
                </template>

                <template v-if="!hasItems">
                    <slot
                        v-bind="{ onAddVehicleCardClick, onCompareSuggestionButtonClick, openCompareModal }"
                        name="suggestions"
                    ></slot>
                </template>
            </loading-wrapper>
        </div>
    </div>
</template>

<script>
import { on } from '@v4/common/utils/dom';
import { dkModalAware } from '@v4/common/components/mixins/modal';
import {
    modalServiceCompareVehicle,
    modalServiceLeadForm,
    modalOneStepLeadForm,
} from '@v4/common/components/mixins/modal-service';
import { buildComparePageURI } from '@v4/compare/utils';
import { getCompareData } from '@v4/services/api/catalogue';
import CompareTable from '@v4/compare/components/organisms/CompareTable.vue';
import LoadingWrapper from '@v4/common/components/atoms/LoadingWrapper.vue';
import { ACTIONS } from '@v4/compare/components/atoms/CompareVehicleMenu.vue';
import { pushViewsProductDetails } from '@v4/common/utils/data-layer';
import gaEventHandler from '@v4/common/components/mixins/google-analytics';
import compareModalHandler from '@v4/common/components/mixins/compare-modal-handler';
import DsPollingWrapper from '@v4/common/components/molecules/DsPollingWrapper';
import { baseUrlMultipleOffers } from '@v4/services/api/v3/offer';
import { stringifyUrl } from '@v4/common/utils/url';
import { EnumFormIds } from '@v4/common/components/organisms/LeadFormWrapper';
import { EventBus } from '@v4/common/utils/event-bus';

export default {
    name: 'ComparePage',
    components: {
        CompareTable,
        LoadingWrapper,
        DsPollingWrapper,
    },
    mixins: [
        dkModalAware,
        modalServiceCompareVehicle,
        modalServiceLeadForm,
        gaEventHandler,
        compareModalHandler,
        modalOneStepLeadForm,
    ],
    props: {
        data: {
            type: Object,
            default: () => ({
                items: [],
                sections: [],
            }),
        },
    },
    data() {
        return {
            alreadyInteracted: false,
            isLoading: false,
            sectionsList: [],
            selectedVehiclesList: [],
            pricingList: [],
        };
    },
    computed: {
        items() {
            if (this.alreadyInteracted) {
                return this.selectedVehiclesList;
            }

            return (this.data && this.data.items) || [];
        },
        sections() {
            if (this.alreadyInteracted) {
                return this.sectionsList;
            }

            return (this.data && this.data.sections) || [];
        },
        hasItems() {
            return this.items && !!this.items.length;
        },
        pricingsPollingUrl() {
            if (!this.hasItems) {
                return '';
            }

            const param = this.items.reduce((acc, item) => {
                const { vehicleHistoricalId } = item;
                let accumulator = acc;
                if (accumulator) {
                    accumulator += '|';
                }
                accumulator += vehicleHistoricalId;
                return accumulator;
            }, '');

            return stringifyUrl({
                url: `${baseUrlMultipleOffers}`,
                query: {
                    vehicleHistoricalId: param,
                    table: 1,
                },
            });
        },
        pricings() {
            return this.pricingList.map((item) => {
                const hasOffers = item.offers && !!item.offers.length;

                return {
                    ...item,
                    offers: (hasOffers && item.offers.filter((offer) => !offer.empty)) || [],
                };
            });
        },
        offersProjectIds() {
            const projectIds = new Set(); // Use a Set to handle uniqueness

            this.pricings.forEach((price) => {
                const hasOffers = price.offers && price.offers.length;

                if (hasOffers) {
                    price.offers.forEach((offer) => {
                        const projectId = (offer.projectId && offer.projectId.toString()) || '';

                        if (projectId) {
                            projectIds.add(projectId); // Add each projectId to the Set
                        }
                    });
                }
            });

            // Return the project IDs as a string, separated by '|'
            return Array.from(projectIds).join('|');
        },
        url() {
            return buildComparePageURI(this.buildComparePageParamsList(this.items));
        },
    },
    mounted() {
        on(window, 'popstate', this.onPopState);

        const documentTitle = this.updateDocumentTitle();
        window.history.replaceState(this.data, documentTitle, this.url);

        if (this.data.events && this.data.events && this.data.events['view_item']) {
            this.pushGtagEvent('view_item', null, this.data.events);
        }

        EventBus.$on('LeadForm:input', this.onLeadFormInput);
    },
    beforeDestroy() {
        EventBus.$off();
    },
    destroyed() {
        /**
         * Don't EVER call 'off' the way below:
         *
         * off(window, 'popstate', this.onPopState);
         *
         * if we do it, ComparePage will never react to state changes again because of switching between
         * duplicated content (PHP or JS).
         * There is also a test for it.
         */
    },
    methods: {
        restartPolling() {
            if (this.$refs.pollingWrapper) {
                this.$refs.pollingWrapper.restart(this.pricingsPollingUrl);
            }
        },
        onPopState(e) {
            if (e && e.state) {
                this.setData(e.state, false);
                this.updateDocumentTitle();
            } else {
                this.setData(
                    {
                        items: [],
                        sections: [],
                    },
                    false
                );
            }
        },
        openLeadModal(versionName) {
            const formData = {
                ...(versionName && { versionName }),
                ...(this.offersProjectIds && { projectIds: this.offersProjectIds }),
            };

            const props = this.leadFormModalProps(versionName, formData);
            const data = {
                ...props,
                subtitle: this.getSubtitleForMultipleVehicles(props.items),
            };

            this.openOneStepLeadFormModal(data);
        },
        onCompareHeroButtonClick() {
            this.pushGaEvent('Compare page', 'click', 'hero start button click');
            this.openCompareModal();
        },
        onCompareSuggestionButtonClick() {
            this.pushGaEvent('Compare page', 'click', 'add first car start button click');
            this.openCompareModal();
        },
        onAddVehicleCardClick(vehicle) {
            this.pushGaEvent('Compare page', 'click', 'default comparison card click');
            this.addVehicle(vehicle);
        },
        async openCompareModal(selectedVehicle = null, startingVehicle = null) {
            const selected = selectedVehicle
                ? {
                    make: selectedVehicle.makeUrlCode,
                    model: selectedVehicle.submodelCommercialUrlCode,
                    submodelId: selectedVehicle.submodelId || selectedVehicle.modelId,
                    version: selectedVehicle.vehicleHistoricalId || selectedVehicle.versionHistoricalId,
                }
                : null;
            const starting = startingVehicle
                ? {
                    make: startingVehicle.makeUrlCode,
                    model: startingVehicle.submodelCommercialUrlCode,
                    submodelId: startingVehicle.submodelId || startingVehicle.modelId,
                    version: startingVehicle.vehicleHistoricalId || startingVehicle.versionHistoricalId,
                }
                : null;

            const vehicle = await this.openCompareVehicleModal(
                selected,
                starting,
                this.items.map((item) => item.submodelCommercialUrlCode),
                '',
                '',
                true
            );

            if (vehicle) {
                if (selected) {
                    this.updateVehicleByIndex(selectedVehicle.index, vehicle);
                } else {
                    this.addVehicle(vehicle);
                }
            }
        },
        async addVehicle(vehicle) {
            const data = await this.getListData(this.items.concat(vehicle));

            if (!data) {
                return;
            }

            this.setData(data);
            this.pushGtagEvent('view_item', null, this.data.events);
            window.scroll({ top: 0, left: 0 });
        },
        async updateVehicleByIndex(index, newVehicle) {
            const data = await this.getListData(
                this.items.map((item, i) => {
                    if (i !== index) {
                        return item;
                    }

                    return newVehicle;
                })
            );

            if (!data) {
                return;
            }

            this.setData(data);
            this.pushGtagEvent('view_item', null, this.data.events);
        },
        async removeVehicleByIndex(index) {
            const data = await this.getListData(this.items.filter((item, i) => i !== index));

            if (!data) {
                return;
            }

            this.setData(data);
            this.pushGtagEvent('view_item', null, this.data.events);
        },
        async getListData(list) {
            const params = this.buildComparePageParamsList(list);

            if (params.length === 0) {
                // If the params array is empty
                // it means that the last vehicle was removed
                // and the compare page returns
                // to the initial state.
                return {
                    items: [],
                    sections: [],
                    events: [],
                };
            }

            this.isLoading = true;

            try {
                const response = await getCompareData(params);
                this.isLoading = false;

                if (response.analytics) {
                    pushViewsProductDetails(response.analytics.products, response.analytics.actionField);
                }

                return response;
            } catch (e) {
                // @TODO
                this.isLoading = false;
                return null;
            }
        },
        setData(response, update = true) {
            this.selectedVehiclesList = response.items;
            this.sectionsList = response.sections;
            this.alreadyInteracted = true;
            this.restartPolling();
            if (update) {
                this.updateLocation(response);
            }
        },
        updateLocation(state) {
            // Example: Compare FIAT Panda and FIAT 500 and Toyota Yaris
            const documentTitle = this.updateDocumentTitle();
            window.history.pushState(state, documentTitle, this.url);
        },
        async onMenuClick({ action, vehicle }) {
            switch (action) {
                case ACTIONS.edit:
                    return this.openCompareModal(vehicle, vehicle);
                case ACTIONS.quote:
                    return this.openLeadModal(vehicle.versionName);
                case ACTIONS.delete:
                    return this.removeVehicleByIndex(vehicle.index);
            }
        },
        leadFormModalProps(versionName, formData = null) {
            return {
                title: this.$t('lead-form-modal.select-deal'),
                items: versionName
                    ? []
                    : this.pricings.map((item, i) => ({
                        ...(this.items[i] || {}),
                        ...item,
                    })),
                formId: EnumFormIds.COMPARE,
                formData,
            };
        },
        updateDocumentTitle() {
            let documentTitle = this.$t('compare.compare-favourite');

            if (this.hasItems) {
                documentTitle = `${this.$t('compare.compare')} ${this.items
                    .map((vehicle) => `${vehicle.makeName} ${vehicle.modelName}`)
                    .join(` ${this.$t('compare.and').toLowerCase()} `)}`;
            }

            document.title = documentTitle;

            return documentTitle;
        },
        onFinalDataLoaded(response) {
            this.pricingList = (response && response.data && response.data.data && response.data.data.items) || [];
        },
        onLeadFormInput() {
            this.pushGtagEvent('begin_checkout', null, this.data.events);
        },
        getSubtitleForMultipleVehicles(vehicles) {
            let formattedTitles = '';

            if (vehicles.length === 1) {
                formattedTitles = `<strong>${vehicles[0].makeName} ${vehicles[0].modelName}</strong>.`;
            } else if (vehicles.length === 2) {
                formattedTitles = `<strong>${vehicles[0].makeName} ${vehicles[0].modelName}</strong> ${this.$t(
                    'compare.and'
                )} <strong>${vehicles[1].makeName} ${vehicles[1].modelName}</strong>.`;
            } else {
                const lastItem = vehicles[vehicles.length - 1];
                formattedTitles = vehicles
                    .map((vehicle) => `<strong>${vehicle.makeName} ${vehicle.modelName}</strong>`)
                    .join(', ');
                formattedTitles += ` ${this.$t('compare.and')} <strong>${lastItem.makeName} ${
                    lastItem.modelName
                }</strong>.`;
            }

            const subtitle = this.$tc('multimodel.you-are-requesting-a-quotation-for', formattedTitles, {
                n: formattedTitles,
            });

            return subtitle;
        },
    },
};
</script>
